import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AdapterService } from 'app/modules/sho_management_pro/finances/services/adapters/adapter.service';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { RidesSalesService } from '../../services/sales/midway/rides-sales.service';
import * as RidesSalesActions from './rides-sales.actions';

@Injectable()
export class RidesSalesEffects {
    private _actions$ = inject(Actions);
    private _ridesSalesService = inject(RidesSalesService);
    private _adapter = inject(AdapterService);
    private _alertService = inject(AlertService);
    private _venueFinancesService = inject(VenueFinancesService);

    messages = {
        getAllError: 'Error getting all rides sales',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All RidesSales
    getAllRidesSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RidesSalesActions.loadRidesSales),
            switchMap((action) => {
                return this._ridesSalesService.getAll(action.playdateId).pipe(
                    map((res) => {
                        const adapted = this._adapter.adaptRidesSalesList(
                            res.data
                        );
                        return RidesSalesActions.loadRidesSalesSuccess({
                            ridesSales: adapted,
                        });
                    }),
                    catchError((error) =>
                        of(
                            RidesSalesActions.loadRidesSalesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One RidesSale
    getRidesSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RidesSalesActions.loadRidesSale),
            switchMap((action) => {
                return this._ridesSalesService.getOne(action.id).pipe(
                    map((res) => {
                        return RidesSalesActions.loadRidesSaleSuccess({
                            ridesSale: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            RidesSalesActions.loadRidesSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create RidesSale
    createRidesSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RidesSalesActions.createRidesSale),
            switchMap((action) => {
                return this._ridesSalesService.create(action.data).pipe(
                    map((res) => {
                        const data = this._adapter.adaptRidesSale(res.data);
                        return RidesSalesActions.createRidesSaleSuccess({
                            ridesSale: data,
                            success: 'Rides sale created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            RidesSalesActions.createRidesSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update RidesSale
    updateRidesSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RidesSalesActions.updateRidesSale),
            switchMap((action) => {
                return this._ridesSalesService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return RidesSalesActions.updateRidesSaleSuccess({
                                ridesSale: this._adapter.adaptRidesSale(
                                    res.data
                                ),
                                success: 'Rides sale updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                RidesSalesActions.updateRidesSaleFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete RidesSale
    deleteRidesSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RidesSalesActions.deleteRidesSale),
            switchMap((action) => {
                return this._ridesSalesService.delete(action.id).pipe(
                    map(() => {
                        return RidesSalesActions.deleteRidesSaleSuccess({
                            id: action.id,
                            success: 'Rides sale deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            RidesSalesActions.deleteRidesSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Sales Rep Commission Rate
    updateRidesSaleSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RidesSalesActions.updateRidesSaleSalesRep),
            switchMap((action) => {
                return this._ridesSalesService
                    .updateSalesRep(action.data, action.id)
                    .pipe(
                        map((res) => {
                            const data = this._adapter.adaptRidesSale(res.data);
                            return RidesSalesActions.updateRidesSaleSalesRepSuccess(
                                {
                                    ridesSale: data,
                                    success: 'Sales rep updated successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                RidesSalesActions.updateRidesSaleSalesRepFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Delete Sales Rep from Ride
    deleteRidesSaleSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RidesSalesActions.deleteRidesSaleSalesRep),
            switchMap((action) => {
                return this._ridesSalesService.deleteSalesRep(action.id).pipe(
                    map((res) => {
                        return RidesSalesActions.deleteRidesSaleSalesRepSuccess(
                            {
                                id: action.id,
                                success: 'Sales rep removed successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            RidesSalesActions.deleteRidesSaleSalesRepFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onRidesSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    RidesSalesActions.createRidesSaleSuccess,
                    RidesSalesActions.updateRidesSaleSuccess,
                    RidesSalesActions.deleteRidesSaleSuccess,
                    RidesSalesActions.updateRidesSaleSalesRepSuccess,
                    RidesSalesActions.deleteRidesSaleSalesRepSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;
                })
            ),
        { dispatch: false }
    );

    onRidesSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    RidesSalesActions.loadRidesSalesFailure,
                    RidesSalesActions.loadRidesSaleFailure,
                    RidesSalesActions.createRidesSaleFailure,
                    RidesSalesActions.updateRidesSaleFailure,
                    RidesSalesActions.deleteRidesSaleFailure,
                    RidesSalesActions.updateRidesSaleSalesRepFailure,
                    RidesSalesActions.deleteRidesSaleSalesRepFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
